import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { api } from '../api';
import { FORMATE, abschnitte, bereichAus, uebergangsStellen, exportTitel, fehlendeAbschnitte, lohnendeAbschnitte, formatKurz, timelineDuration, jumpFrames, keyAction, nextCut, snapToFrame, transformAt, type Op } from '@schnitt/shared';
import { useEditor } from '../store';
import { ContextMenu, type MenuItem } from '../components/ContextMenu';
import { useKeyScheme } from '../keyScheme';
import { zeitText } from '../zeit';
import { Preview } from '../components/Preview';
import { Timeline } from '../components/Timeline';
import { Transport } from '../components/Transport';
import { MediaSheet } from '../components/MediaSheet';
import { Ablegen } from '../components/Ablegen';
import { AlteAenderungenSheet } from '../components/AlteAenderungenSheet';
import { BefehlSheet, type BefehlMitTun } from '../components/BefehlSheet';
import { InspectorSheet } from '../components/InspectorSheet';
import { AudioSheet } from '../components/AudioSheet';
import { SpurSheet } from '../components/SpurSheet';
import { BereichSheet } from '../components/BereichSheet';
import { MarkensetAssistent } from '../components/MarkensetAssistent';
import { VerlaufSheet } from '../components/VerlaufSheet';
import { BrandSheet } from '../components/BrandSheet';
import { BrandKitSheet } from '../components/BrandKitSheet';
import { ProjectSettingsSheet } from '../components/ProjectSettingsSheet';
import { TeilenSheet } from '../components/TeilenSheet';
import { Pegelmesser, usePegelmesserAn } from '../components/Pegelmesser';
import { TranscriptSheet } from '../components/TranscriptSheet';
import { DiashowSheet } from '../components/DiashowSheet';
import { ExportSheet } from '../components/ExportSheet';
import { ClipTools, PasteBar, QuellenLeiste, Toolbar, type SheetName } from '../components/Toolbar';
import { duplicateClip, pasteClips, splitAtPlayhead } from '../edits';
import { VoiceoverSheet } from '../components/VoiceoverSheet';
import { UebergangSheet } from '../components/UebergangSheet';
import { MessgeraeteSheet } from '../components/MessgeraeteSheet';
import { UndoRedo } from '../components/UndoRedo';
import { useTitel } from '../titel';
import { FeedbackSheet } from '../components/FeedbackSheet';
import { KommentarSheet } from '../components/KommentarSheet';
import { EinfuegenSheet } from '../components/EinfuegenSheet';
import { Icon } from '../components/Icon';
import { leisteBleibt, useVollbild } from '../vollbild';
import { ladeFilmeVor, ladeVor } from '../vorladen';
import { fehlerMelden } from '../fehlerMelden';
import { neustartMelden, neustartPruefen, standMerken } from '../neustart';
import { nurZuDenMedien, platziere } from '../platzieren';
import { loescheClips } from '../loeschen';

/**
 * So lange muss Ruhe sein, bevor im Hintergrund gerechnet wird (7.71d) --
 * kein Zug am Schnitt, kein Abspielen. Wer schneidet, wirft mit jeder
 * Änderung den Abschnitt weg, an dem gerade gerechnet würde.
 */
const RUHE_VORM_RECHNEN = 20_000;

export function Editor({
  slug,
  onBack,
}: {
  slug: string;
  onBack: () => void;
}): ReactElement {
  const open = useEditor((s) => s.open);
  const close = useEditor((s) => s.close);
  const project = useEditor((s) => s.project);
  // 7.69n: die Vorschau-Dateien vorladen, sobald das Projekt und seine
  // Medien da sind -- in der Reihenfolge, in der sie im Schnitt drankommen.
  const assets = useEditor((s) => s.assets);
  const tracksFuerVorladen = useEditor((s) => s.timeline.tracks);
  useEffect(() => {
    if (!project) return;
    const reihenfolge = tracksFuerVorladen
      .flatMap((t) => t.clips)
      .sort((a, b) => a.start - b.start)
      .map((c) => (c.source.kind === 'asset' ? c.source.assetId : ''))
      .filter(Boolean);
    const t = window.setTimeout(() => void ladeVor(assets, reihenfolge), 1500);
    return () => window.clearTimeout(t);
    // Nur, wenn sich die Menge der fertigen Medien ändert -- nicht bei jedem Schnitt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, assets.map((a) => `${a.id}:${a.status}`).join(',')]);
  /**
   * Und die gerechneten Abschnitte dazu (7.69t).
   *
   * Der Befund vom iPhone (11. September, 01:09) sagte es deutlich: acht
   * Sekunden Wartezeit mit `takt: film`, während daneben „8 Dateien
   * vorgeladen“ stand. Das Vorladen kannte nur Medien -- der Film, also genau
   * die Datei, die beim Abspielen läuft, wurde kalt geholt. Jetzt wandert
   * jeder fertige Abschnitt denselben Weg: Zwischenspeicher, Arbeitsspeicher.
   */
  const filmeZumVorladen = useEditor((s) =>
    s.renders
      .filter((r) => r.abdruck && r.status === 'ready' && r.url)
      .map((r) => r.url!)
      .join(','),
  );
  useEffect(() => {
    if (!project || !filmeZumVorladen) return;
    const urls = filmeZumVorladen.split(',');
    const t = window.setTimeout(() => void ladeFilmeVor(urls), 800);
    return () => window.clearTimeout(t);
  }, [project?.id, filmeZumVorladen]);
  const connection = useEditor((s) => s.connection);
  const ungesichert = useEditor((s) => s.offenSatz);
  const peers = useEditor((s) => s.peers);
  /** Andere Menschen -- und getrennt davon die eigenen weiteren Fenster. */
  const kollegen = peers.filter((p) => !p.selbst);
  const ichSelbstNochmal = peers.length - kollegen.length;
  // Die Belegung ist eine Sache des Geräts, nicht des Kontos: am Schnittplatz
  // sitzt, wer dort sitzt.
  const scheme = useKeyScheme();
  const undo = useEditor((s) => s.undo);
  const redo = useEditor((s) => s.redo);
  const role = useEditor((s) => s.role);
  const [feedback, setFeedback] = useState(false);
  const vollbild = useVollbild();
  /** Der Satz zur Browser-Leiste kommt einmal, nicht bei jedem Tippen. */
  const leisteGesagt = useRef(false);
  const toast = useEditor((s) => s.toast);
  const dispatch = useEditor((s) => s.dispatch);
  const einfuegeMedium = useEditor((s) => s.einfuegeMedium);
  const platzieren = useEditor((s) => s.platzieren);
  const setPlatzieren = useEditor((s) => s.setPlatzieren);
  // Der Reiter sagt, welches Projekt hier offen ist -- und wie weit ein
  // Export ist, denn während der läuft, schaut man woanders hin.
  const renders = useEditor((s) => s.renders);
  const offeneHinweise = useEditor((s) => s.kommentare.filter((k) => !k.parent_id && !k.resolved_at).length);
  // Was seit dem letzten Hinsehen dazugekommen ist und nicht von einem selbst
  // stammt. Das ist die Zahl, die einen wirklich angeht: „drei offene“ sagt
  // nichts darüber, ob etwas *Neues* dabei ist (7.8d).
  const neueHinweise = useEditor((s) => s.neueHinweise);
  const zugangWeg = useEditor((s) => s.zugangWeg);
  const hinweiseGesehen = useEditor((s) => s.hinweiseGesehen);
  useTitel(project?.name ?? 'Schnittplatz', exportTitel(renders));

  const [sheet, setSheet] = useState<SheetName>(null);
  const pegelAn = usePegelmesserAn();
  /** Aus den Projekt-Einstellungen geöffnet, führt „Zurück“ dorthin. */
  const [teilenAusProjekt, setTeilenAusProjekt] = useState(false);
  // Von der Startseite aus „Einstellungen …“ (7.217): das Projekt geht mit
  // seinen Einstellungen auf, sobald es geladen ist -- und nur einmal, ein
  // Neuladen danach öffnet den Schnittplatz wie sonst.
  const blattBeimOeffnen = useRef(new URLSearchParams(location.search).get('blatt'));
  useEffect(() => {
    if (!project || blattBeimOeffnen.current !== 'projekt') return;
    blattBeimOeffnen.current = null;
    history.replaceState(null, '', location.pathname);
    setSheet('project');
  }, [project]);
  // Aus einer Nachricht oder Mail zu einem Hinweis (7.276): `?hinweis=<id>`
  // öffnet das Blatt bei genau diesem Hinweis. Die anderen Angaben der
  // Adresse bleiben -- ein Gast-Link trägt dort seinen Schlüssel.
  const hinweisBeimOeffnen = useRef(new URLSearchParams(location.search).get('hinweis'));
  const [hinweisFokus, setHinweisFokus] = useState<string | null>(null);
  useEffect(() => {
    const id = hinweisBeimOeffnen.current;
    if (!project || !id) return;
    hinweisBeimOeffnen.current = null;
    const rest = new URLSearchParams(location.search);
    rest.delete('hinweis');
    history.replaceState(null, '', location.pathname + (rest.size ? `?${rest.toString()}` : ''));
    setHinweisFokus(id);
    setSheet('kommentare');
    void hinweiseGesehen();
    // hinweiseGesehen ist eine feste Funktion aus dem Store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);
  /** Suchen und ausführen (S27). */
  const [befehlsfeld, setBefehlsfeld] = useState(false);
  /**
   * Was man tippen kann (S27). Die Liste steht hier, weil hier die Handlungen
   * stehen -- gesucht wird im gemeinsamen Teil, ausgeführt an der Stelle, an
   * der es auch der Knopf täte. Kein zweiter Weg zu derselben Sache: jeder
   * Eintrag ruft, was der Werkzeugkasten ruft.
   *
   * `moeglich` statt eines grauen Eintrags: was jetzt nicht geht, soll nicht
   * dastehen. Die Reihenfolge ist eine Aussage -- oben, was man am häufigsten
   * sucht -- und sie bleibt bei gleichem Rang erhalten.
   */
  const befehle: BefehlMitTun[] = (() => {
    const s = useEditor.getState();
    const darf = role !== 'viewer';
    const eineAuswahl = s.selection.length === 1;
    const blatt = (name: SheetName) => () => setSheet(name);
    return [
      { id: 'trennen', name: 'Trennen', gruppe: 'Schnitt', stichworte: ['schneiden', 'teilen', 'split'], taste: 'S',
        moeglich: darf, tun: () => {
          const st = useEditor.getState();
          const ops = splitAtPlayhead(st.timeline, st.playhead, st.selection);
          if (ops.length) st.dispatch(ops);
          else st.toast('info', 'Erst den Abspielkopf auf den Clip stellen.');
        } },
      { id: 'duplizieren', name: 'Duplizieren', gruppe: 'Schnitt', stichworte: ['kopie', 'doppeln'],
        moeglich: darf && eineAuswahl, tun: () => {
          const st = useEditor.getState();
          const doppelt = duplicateClip(st.timeline, st.selection[0]!);
          if (doppelt) { st.dispatch(doppelt.op); st.select([doppelt.id]); }
        } },
      { id: 'rueckgaengig', name: 'Rückgängig', gruppe: 'Schnitt', stichworte: ['undo', 'zurueck'], taste: '⌘Z',
        moeglich: darf, tun: undo },
      { id: 'wiederholen', name: 'Wiederherstellen', gruppe: 'Schnitt', stichworte: ['redo', 'nochmal'],
        moeglich: darf, tun: redo },
      { id: 'medien', name: 'Medien', gruppe: 'Material', stichworte: ['hochladen', 'video', 'bild', 'ton', 'import'],
        tun: blatt('media') },
      { id: 'diashow', name: 'Diashow aus Bildern', gruppe: 'Material', stichworte: ['bilder', 'fotos', 'slideshow'],
        moeglich: darf, tun: blatt('diashow') },
      { id: 'text', name: 'Text einblenden', gruppe: 'Einblendungen', stichworte: ['titel', 'bauchbinde', 'insert'],
        moeglich: darf, tun: blatt('brand') },
      { id: 'marke', name: 'Markenset', gruppe: 'Einblendungen', stichworte: ['logo', 'farben', 'schrift', 'brand'],
        tun: blatt('brandKit') },
      { id: 'untertitel', name: 'Untertitel & Transkript', gruppe: 'Ton', stichworte: ['srt', 'vtt', 'abschrift', 'transkript'],
        tun: blatt('transcript') },
      { id: 'tonblatt', name: 'Ton', gruppe: 'Ton', stichworte: ['pegel', 'lautstaerke', 'musik'], tun: blatt('audio') },
      { id: 'voiceover', name: 'Aufnehmen', gruppe: 'Ton', stichworte: ['mikrofon', 'sprecher', 'voiceover'],
        moeglich: darf, tun: blatt('voiceover') },
      { id: 'uebergaenge', name: 'Übergänge', gruppe: 'Schnitt', stichworte: ['blende', 'übergang', 'wischen', 'crossfade'],
        moeglich: darf, tun: () => useEditor.getState().setUebergangsModus(true) },
      { id: 'messgeraete', name: 'Messgeräte', gruppe: 'Prüfen', stichworte: ['histogramm', 'vektorskop', 'parade', 'waveform'],
        tun: blatt('messgeraete') },
      { id: 'raster', name: 'Raster und Schutzbereich', gruppe: 'Prüfen', stichworte: ['drittel', 'hilfslinien', 'safe area'],
        tun: () => {
          const st = useEditor.getState();
          st.setRaster(st.raster === 'aus' ? 'beides' : 'aus');
          st.toast('info', st.raster === 'aus' ? 'Raster: Beides.' : 'Raster aus.');
        } },
      { id: 'export', name: 'Exportieren', gruppe: 'Ausgeben', stichworte: ['rechnen', 'ausspielen', 'render'],
        tun: blatt('export') },
      { id: 'projekt', name: 'Projekt-Einstellungen', gruppe: 'Projekt', stichworte: ['format', 'bildrate', 'notizen', 'lautheit'],
        tun: blatt('project') },
      { id: 'hinweise', name: 'Hinweise', gruppe: 'Projekt', stichworte: ['kommentare', 'anmerkungen'], tun: blatt('kommentare') },
      { id: 'verlauf', name: 'Verlauf', gruppe: 'Projekt', stichworte: ['protokoll', 'aenderungen', 'history'], tun: blatt('verlauf') },
      { id: 'teilen', name: 'Zusammenarbeiten', gruppe: 'Projekt', stichworte: ['teilen', 'zugriff', 'einladen', 'rechte', 'gast', 'link', 'team'],
        tun: blatt('teilen') },
    ];
  })();
  const spurBlatt = useEditor((s) => s.spurBlatt);
  const bereichBlatt = useEditor((s) => s.bereichBlatt);
  const setSpurBlatt = useEditor((s) => s.setSpurBlatt);
  // The brand kit editor opens from the toolbar and from inside the text
  // sheet; closing it goes back to wherever it came from.
  const [kitReturnsTo, setKitReturnsTo] = useState<SheetName>(null);
  /** Ein Blatt „Spur“, das aus einem anderen kam, geht dorthin zurück. */
  const [spurReturnsTo, setSpurReturnsTo] = useState<SheetName>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let standAus: (() => void) | null = null;
    void (async () => {
      try {
        const { project, role, gastName } = await api.projectBySlug(slug);
        if (cancelled) return;
        // Vor dem Öffnen nachsehen: danach schreibt der Tab seinen neuen Stand.
        const neustart = neustartPruefen(slug);
        await open(project, role, gastName ?? '');
        if (cancelled) return;
        // Jedes Neuladen geht an die Diagnose (7.258): „neuladen“ oder
        // „absturz“.
        if (neustart) neustartMelden(project.id, slug, neustart);
        // **Hat der Browser die Seite abgeschossen** (7.259) -- am iPhone nach
        // dem Sperren, wenn ein Tab mit viel Video zu viel Speicher hält --,
        // steht der Kopf danach wieder dort, wo er war. Ein gewolltes
        // Neuladen fängt vorn an wie bisher: dort hatte das Zurückstellen im
        // Prüfstand das Abspielen danach in den Filmmodus gebracht, und das
        // Herunterziehen, der andere ungewollte Weg, ist seit 7.258 zu.
        if (neustart?.art === 'absturz') {
          const st = useEditor.getState();
          const kopf = Math.min(neustart.kopf, Math.max(0, timelineDuration(st.timeline)));
          if (kopf > 0) {
            st.setPlayhead(kopf);
            const s = Math.floor(kopf);
            st.toast('info', `Der Browser hatte die Seite neu geladen -- weiter bei ${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}.`);
          }
        }
        standAus = standMerken(slug);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Projekt konnte nicht geöffnet werden.');
        // Woran es scheiterte, gehört in die Diagnose (7.263) -- sonst steht
        // die Meldung nur auf dem Schirm dessen, dem es passiert.
        fehlerMelden('oeffnen', err);
      }
    })();
    return () => {
      cancelled = true;
      standAus?.();
      close();
    };
  }, [slug, open, close]);

  /**
   * Tastenbefehle für alle am Rechner; das Telefon sieht davon nichts.
   *
   * Welche Taste was tut, steht in `@schnitt/shared/keys` -- eine Tabelle je
   * Belegung, damit man sie prüfen *und* anzeigen kann. Hier steht nur noch,
   * was eine Handlung bewirkt.
   */
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      // Suchen und ausführen (S27) hört *vor* der Feld-Sperre zu: ⌘K soll auch
      // gehen, wenn der Finger gerade in einem Feld steht -- das ist der
      // häufige Fall, und es ist die Taste, die überall dafür gilt.
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setBefehlsfeld((offen) => !offen);
        return;
      }
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      const action = keyAction(scheme, {
        key: event.key,
        meta: event.metaKey,
        ctrl: event.ctrlKey,
        shift: event.shiftKey,
        alt: event.altKey,
      });
      if (!action) return;
      const s = useEditor.getState();
      const fps = s.timeline.settings.fps;
      const darf = s.role !== 'viewer';
      const springe = (frames: number) => s.setPlayhead(Math.max(0, snapToFrame(s.playhead + frames / fps, fps)));

      switch (action) {
        case 'play':
          event.preventDefault();
          s.setPlaying(!s.playing);
          return;
        case 'undo':
          event.preventDefault();
          undo();
          return;
        case 'redo':
          event.preventDefault();
          redo();
          return;
        case 'frameBack':
          event.preventDefault();
          springe(-1);
          return;
        case 'frameForward':
          event.preventDefault();
          springe(1);
          return;
        case 'jumpBack':
          event.preventDefault();
          springe(-jumpFrames(scheme, fps));
          return;
        case 'jumpForward':
          event.preventDefault();
          springe(jumpFrames(scheme, fps));
          return;
        case 'prevCut':
        case 'nextCut': {
          event.preventDefault();
          const ziel = nextCut(s.timeline, s.playhead, action === 'nextCut' ? 1 : -1);
          if (ziel !== null) s.setPlayhead(ziel);
          return;
        }
        case 'copy':
          if (s.selection.length === 0 || !darf) return;
          event.preventDefault();
          s.copySelection();
          return;
        case 'paste': {
          if (s.clipboard.length === 0 || !darf) return;
          event.preventDefault();
          const { ops, ids } = pasteClips(s.timeline, s.clipboard, s.playhead);
          if (ops.length) {
            s.dispatch(ops);
            s.select(ids);
          }
          return;
        }
        case 'duplicate': {
          if (s.selection.length !== 1 || !darf) return;
          event.preventDefault();
          const copy = duplicateClip(s.timeline, s.selection[0]!);
          if (copy) {
            s.dispatch(copy.op);
            s.select([copy.id]);
          }
          return;
        }
        case 'split': {
          if (!darf) return;
          event.preventDefault();
          const ops = splitAtPlayhead(s.timeline, s.playhead, s.selection);
          if (ops.length) s.dispatch(ops);
          else s.toast('info', 'Erst den Abspielkopf auf den Clip stellen.');
          return;
        }
        // Anfang und Ende eines Bereichs -- I und O, wie überall sonst
        // (18. September: „Bereiche müssen besser erreichbar sein“). Sie
        // liegen an derselben Stelle wie im Menü der Leiste und in der
        // Transportleiste; nur der Weg ist kürzer.
        case 'bereichVon':
        case 'bereichBis': {
          event.preventDefault();
          const ende = timelineDuration(s.timeline);
          const jetzt = s.bereich;
          const neu =
            action === 'bereichVon'
              ? bereichAus(s.playhead, jetzt?.bis ?? ende, ende, 1 / fps)
              : bereichAus(jetzt?.von ?? 0, s.playhead, ende, 1 / fps);
          if (!neu) {
            s.toast('info', action === 'bereichVon' ? 'Dahinter ist nichts mehr.' : 'Davor ist nichts.');
            return;
          }
          s.setBereich(neu);
          s.toast('success', `Bereich ${zeitText(neu.von)} bis ${zeitText(neu.bis)}.`);
          return;
        }
        case 'keyframe': {
          if (s.selection.length !== 1 || !darf) return;
          const found = s.clipById(s.selection[0]!);
          if (!found) return;
          event.preventDefault();
          const local = Math.min(found.clip.duration, Math.max(0, s.playhead - found.clip.start));
          const transform = transformAt(found.clip, local);
          const rest = found.clip.keyframes.filter((k) => Math.abs(k.at - local) >= 1 / fps);
          s.dispatch({ op: 'updateClip', clipId: found.clip.id, patch: { keyframes: [...rest, { at: local, transform }] } } as Op);
          s.toast('success', 'Keyframe gesetzt.');
          return;
        }
        case 'delete': {
          if (!darf) return;
          // Ist ein Keyframe angewählt, ist er gemeint -- nicht der Clip, auf
          // dem er sitzt. Vorher verschwand bei der Rücktaste „einfach das
          // ganze Element“ (Sascha, 7. September), und das ist der grösste
          // Unterschied zwischen „ich nehme diese eine Bewegung zurück“ und
          // „ich lösche die Aufnahme“.
          const wahl = s.keyframeWahl;
          if (wahl) {
            const gefunden = s.clipById(wahl.clipId);
            const bleibt = gefunden?.clip.keyframes.filter((k) => Math.abs(k.at - wahl.at) >= 1e-6);
            if (gefunden && bleibt && bleibt.length !== gefunden.clip.keyframes.length) {
              event.preventDefault();
              s.dispatch({ op: 'updateClip', clipId: wahl.clipId, patch: { keyframes: bleibt } } as Op);
              s.waehleKeyframe(null);
              s.toast('success', 'Keyframe entfernt.');
              return;
            }
            s.waehleKeyframe(null);
          }
          if (s.selection.length === 0) return;
          event.preventDefault();
          const anzahl = loescheClips(s.selection);
          // Löschen ist der Griff, den man am häufigsten bereut. Der Satz sagt,
          // dass er sich zurücknehmen lässt, statt dass man es hofft.
          if (anzahl > 0) s.toast('info', anzahl > 1 ? `${anzahl} Clips gelöscht — „Rückgängig“ holt sie zurück.` : 'Clip gelöscht — „Rückgängig“ holt ihn zurück.');
          return;
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo, scheme]);

  // Hochkant heisst am breiten Schirm: die Leinwand ist schmal, links und
  // rechts liegt Platz brach. Dann duerfen die Glasleisten dorthin, statt
  // oben und unten Bildhoehe zu kosten (7.43, nach Saschas Meldung vom
  // 7. September). Bei Querformat begrenzt die Breite, und dort waere eine
  // Leiste an der Seite genau der falsche Ort.
  const format = useEditor((s) => s.timeline.settings);
  const hochkant = format.height > format.width;
  // 7.24b: die Formatwahl am Bild. Der Knopf sagt, was gilt, und ein Tipp
  // öffnet die Liste -- dieselbe wie im Projektblatt.
  const [formatMenue, setFormatMenue] = useState<{ x: number; y: number } | null>(null);
  const refreshRenders = useEditor((s) => s.refreshRenders);
  /**
   * Die Abschnitte, im Hintergrund (7.71b, 7.71d).
   *
   * Sascha, 10. September: „Können wir das nicht intelligenter und ohne knopf
   * machen? Im Hintergrund? Und wenn man play drückt macht es pause oderso.“
   * Also: kein Knopf, keine Meldung, kein Modus. Gerechnet wird, was es
   * braucht -- zwei Videos übereinander, eine Blende --, und nur, während
   * niemand abspielt. Was fertig ist, spielt die Vorschau von selbst; was
   * fehlt, setzt sie live zusammen. Zu sehen ist das an der schmalen Leiste
   * über der Zeitleiste, sonst nirgends.
   */
  const timelineJetzt = useEditor((s) => s.timeline);
  const spieltGerade = useEditor((s) => s.playing);
  const uebergangsModus = useEditor((s) => s.uebergangsModus);
  const setUebergangsModus = useEditor((s) => s.setUebergangsModus);
  const assetsJetzt = useEditor((s) => s.assets);
  const filmAbschnitte = useMemo(() => {
    const videos = new Set(assetsJetzt.filter((a) => a.kind === 'video').map((a) => a.id));
    return lohnendeAbschnitte(timelineJetzt, abschnitte(timelineJetzt), (id: string) => videos.has(id));
  }, [timelineJetzt, assetsJetzt]);
  const filmFehlt = useMemo(() => fehlendeAbschnitte(renders, filmAbschnitte).length, [renders, filmAbschnitte]);
  /**
   * Gerechnet wird in der Ruhe, nicht im Schnitt.
   *
   * Wer gerade schneidet, ändert den Schnitt -- und jede Änderung wirft den
   * Abschnitt weg, an dem gerade gerechnet wird. Also wird gewartet, bis eine
   * Weile nichts passiert ist: kein Zug, kein Tipp, kein Abspielen. Dann
   * rechnet der Dienst nach, und wenn wieder jemand anfasst, hört er beim
   * nächsten Abschnitt von selbst auf.
   */
  const revJetzt = useEditor((s) => s.rev);
  const letzteRegung = useRef(Date.now());
  useEffect(() => {
    letzteRegung.current = Date.now();
  }, [revJetzt, spieltGerade]);
  const zuletztBestellt = useRef(0);
  useEffect(() => {
    if (!project || role === 'viewer' || filmFehlt === 0) return;
    // Beim Abspielen wird nicht bestellt: dann gehört die Leitung dem Bild.
    if (spieltGerade) return;
    const versuche = () => {
      const jetzt = Date.now();
      if (jetzt - letzteRegung.current < RUHE_VORM_RECHNEN) return;
      if (jetzt - zuletztBestellt.current < 4000) return;
      if (useEditor.getState().playing) return;
      zuletztBestellt.current = jetzt;
      void (async () => {
        try {
          await api.abschnitteRechnen(project.id, 3);
          await refreshRenders();
        } catch {
          // Vorrechnen ist Nebensache: schlägt es fehl, setzt die Vorschau
          // weiter live zusammen. Beim nächsten Mal wieder.
        }
      })();
    };
    const uhr = setInterval(versuche, 2000);
    versuche();
    return () => clearInterval(uhr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, role, spieltGerade, filmFehlt, renders]);

  /**
   * „Rechne den Rest, ich gehe“ (7.71f) -- der letzte offene Punkt an 7.71b.
   *
   * Bestellt hat bisher nur der Browser. Wer das Telefon weglegt, lässt den
   * halben Film ungerechnet liegen und findet beim nächsten Mal genau dort
   * wieder eine stockende Vorschau.
   *
   * Es gibt keinen Knopf dafür: das wäre gegen 7.71d („kein Knopf, kein
   * Modus“), und niemand will gefragt werden, ob der Rechner weiterrechnen
   * soll. Also sagt die App es beim Weggehen selbst -- und beim Zurückkommen
   * nimmt sie es zurück, weil dann wieder sie bestellt und zwei Besteller
   * doppelt bestellten.
   */
  useEffect(() => {
    if (!project || role === 'viewer') return;
    const projektId = project.id;
    const gehtWeg = () => {
      // Nur wenn wirklich etwas fehlt: eine Anfrage, die nichts bestellt,
      // ist eine Anfrage zu viel.
      if (filmFehlt === 0) return;
      void api.abschnitteWeiter(projektId, useEditor.getState().rev).catch(() => {
        // Nebensache: ohne den Auftrag rechnet beim nächsten Öffnen wieder
        // der Browser nach.
      });
    };
    const istWiederDa = () => {
      void api.abschnitteNichtWeiter(projektId).catch(() => {});
    };
    const wechsel = () => (document.visibilityState === 'hidden' ? gehtWeg() : istWiederDa());
    document.addEventListener('visibilitychange', wechsel);
    // Safari auf dem iPhone schickt beim Wegwischen kein `visibilitychange`,
    // sondern `pagehide` -- und das ist genau der Fall, um den es geht. Hier
    // wird nicht nach der Sichtbarkeit gefragt: wer geht, geht.
    window.addEventListener('pagehide', gehtWeg);
    return () => {
      document.removeEventListener('visibilitychange', wechsel);
      window.removeEventListener('pagehide', gehtWeg);
    };
  }, [project, role, filmFehlt]);

  const formatItems: MenuItem[] = [
    ...FORMATE.map((f) => ({
      icon: (f.width === format.width && f.height === format.height ? 'check' : 'film') as string,
      label: `${f.label} · ${f.hint}`,
      onSelect: () => {
        if (f.width === format.width && f.height === format.height) return;
        dispatch({ op: 'updateSettings', patch: { width: f.width, height: f.height } } as Op);
        toast('success', `Bildformat ${f.label}.`);
      },
    })),
    { icon: 'sliders', label: 'Eigenes Mass…', onSelect: () => project && setSheet('project') },
  ];

  // **Erst nach allen Hooks** (7.263; Veronika, 22. Oktober: „des lädt
  // net -- Bildschirm schwarz“, am 26. Oktober über das Auffangnetz:
  // „Minified React error #300“). Dieser Ausstieg stand vor rund dreissig
  // Hooks. Öffnete das Projekt erst und scheiterte danach ein Nachladen in
  // `open()`, rief der nächste Aufbau weniger Hooks auf als der erste --
  // React brach ab, und statt dieser Seite blieb der Schirm schwarz.
  if (error) {
    return (
      <div className="page" data-oeffnen-fehler>
        <h1>Das hat nicht geklappt</h1>
        <p className="lede">{error}</p>
        <button onClick={onBack}>Zurück zur Übersicht</button>
      </div>
    );
  }

  return (
    <div className={`app${hochkant ? ' hochkant' : ''}${vollbild.an ? ' vollbild' : ''}`}>
      {/* Im Vollbild ist die Kopfleiste weg -- der Weg zurück schwebt dann
          über dem Bild. */}
      {vollbild.an ? (
        <button className="vollbild-raus" onClick={vollbild.umschalten} aria-label="Vollbild verlassen" title="Vollbild verlassen (Esc)">
          <Icon name="fullscreenExit" size={20} />
        </button>
      ) : null}
      {/* Link widerrufen oder aus dem Projekt genommen (7.280): die
          Verbindung ist zu, und das soll man sehen, nicht nur eine Meldung
          lang. Was danach noch geändert wird, käme nicht mehr an. */}
      {zugangWeg ? (
        <div className="fehler-zeile zugang-weg" role="alert" data-zugang-weg>
          Dein Zugang zu diesem Projekt wurde zurückgenommen. Was du hier noch änderst, wird nicht gespeichert.
        </div>
      ) : null}
      <div className="topbar">
        <button className="ghost icon-btn" onClick={onBack} aria-label="Zurück">
          ‹
        </button>
        {/* The title doubles as the way into settings: the bar has no room
            for another button, and tapping the name is where people look. */}
        <h1
          onClick={() => project && setSheet('project')}
          style={{ cursor: project ? 'pointer' : 'default' }}
          title="Projekt-Einstellungen"
        >
          {project?.name ?? 'Lädt…'}
          {/* Der Stift sagt, dass der Name anfassbar ist. Vorher musste man
              raten, dass eine Überschrift ein Knopf ist. */}
          {project ? <span className="stift" aria-hidden="true">✎</span> : null}
          {project?.archived ? <span className="badge" style={{ marginLeft: 8 }}>archiviert</span> : null}
        </h1>
        {/* Die eigene Verbindung ist gar nicht mehr in der Liste -- das
            entscheidet der Server, jede Verbindung bekommt ihre eigene Sicht
            (Sascha, 7. September: „ich arbeite ja nicht mit mir selber
            zusammen“). Bleibt der Fall, dass derselbe Mensch den Schnitt
            zweimal offen hat: das ist kein Mitarbeiten, aber eine Warnung
            wert -- und die steht als Zeichen da, nicht als Kollege. */}
        <div className="peers">
          {/* Keyed by position as well: the same person on a phone and a
              laptop is two peers with one user id. */}
          {kollegen.slice(0, 4).map((peer, i) =>
            peer.avatarAt ? (
              <img
                key={`${peer.userId}-${i}`}
                className="peer-chip"
                src={`/api/users/${peer.userId}/bild?v=${encodeURIComponent(peer.avatarAt)}`}
                alt={peer.name}
                title={peer.name}
                // Der Rand in ihrer Farbe: das Bild sagt wer, die Farbe sagt
                // welcher Abspielkopf unten dazugehört.
                style={{ borderColor: peer.color }}
                data-peer-bild
              />
            ) : (
              <span key={`${peer.userId}-${i}`} className="peer-chip" style={{ background: peer.color }} title={peer.name}>
                {initials(peer.name)}
              </span>
            ),
          )}
          {ichSelbstNochmal ? (
            <span
              className="peer-chip eigenes-fenster"
              title={`Du hast diesen Schnitt noch ${ichSelbstNochmal === 1 ? 'in einem weiteren Fenster' : `in ${ichSelbstNochmal} weiteren Fenstern`} offen. Änderungen von dort kommen hier an — und umgekehrt.`}
              data-eigenes-fenster={ichSelbstNochmal}
            >
              ⧉
            </span>
          ) : null}
        </div>
        {/* **Zusammenarbeiten steht bei den Köpfen** (Sascha, 27. Oktober: „teilen passt
            ja eigentlich ganz gut in diese Linie? an der Stelle, wo man
            eigentlich sieht, wer gerade so am Start ist“). Vorher lag es im
            Export-Blatt, im zweiten Reiter. */}
        {project ? (
          <button
            className="ghost icon-btn"
            onClick={() => {
              setTeilenAusProjekt(false);
              setSheet('teilen');
            }}
            aria-label="Zusammenarbeiten"
            title="Zusammenarbeiten: Team und Externe"
            data-teilen-oeffnen
          >
            <Icon name="teilen" size={18} />
          </button>
        ) : null}
        {/* Was noch nicht gesichert ist (S28), steht neben dem Punkt und nicht
            in einer Meldung, die weggeht: „drei Änderungen liegen noch hier“
            ist ein Zustand und keine Nachricht. */}
        <span
          className={`dot ${connection} ${ungesichert ? 'offen' : ''}`.trim()}
          title={ungesichert ? `${CONNECTION_LABEL[connection]} — ${ungesichert}` : CONNECTION_LABEL[connection]}
          data-ungesichert={ungesichert ?? ''}
        />
        {/* Melden geht auch von hier. Vorher hing es nur an der Startseite --
            und was einem beim Schneiden auffällt, fällt einem beim Schneiden
            auf, nicht zwei Seiten später. */}
        {/* Hinweise am Zeitpunkt: die Zahl sagt, wie viele offen sind -- sonst
            liegt eine Anmerkung im Schnitt und niemand sieht sie. */}
        <button
          className={`ghost icon-btn ${neueHinweise ? 'neu' : offeneHinweise ? 'meldet' : ''}`.trim()}
          onClick={() => {
            setSheet('kommentare');
            void hinweiseGesehen();
          }}
          aria-label="Hinweise"
          title={
            neueHinweise
              ? `${neueHinweise} neu seit deinem letzten Blick`
              : offeneHinweise
                ? `${offeneHinweise} offene Hinweise`
                : 'Hinweise am Zeitpunkt'
          }
        >
          <Icon name="bell" size={18} />
          {neueHinweise ? (
            <span className="zaehler neu" data-neu>{neueHinweise}</span>
          ) : offeneHinweise ? (
            <span className="zaehler">{offeneHinweise}</span>
          ) : null}
        </button>
        {/* Zwei verschiedene Dinge, und sie sahen aus wie eines: das
            Klemmbrett stand für Hinweise im Schnitt, das Untertitel-Kästchen
            daneben für „etwas melden“ (Sascha, 7. September: „Was ist
            Feedback, was ist Nutzerkommentar“). Jetzt ist die Glocke, was
            Menschen im Projekt geschrieben haben, und das Fragezeichen, was
            an uns geht. */}
        <button
          className="ghost icon-btn"
          onClick={() => setFeedback(true)}
          aria-label="Hilfe und Rückmeldung"
          title="Etwas melden oder vorschlagen"
        >
          <Icon name="help" size={18} />
        </button>
        <button
          className="ghost icon-btn"
          onClick={() => {
            // Ein offenes Blatt im Vollbild waere ein Blatt ohne Weg zurueck.
            setSheet(null);
            // Und einmal sagen, was hier nicht geht: auf dem iPhone kennt
            // Safari kein Vollbild fuer eine Leinwand, die Adresszeile bleibt
            // also stehen. Lieber der ehrliche Satz mit dem Weg dorthin als
            // ein Knopf, der weniger tut, als er verspricht.
            if (!vollbild.an && leisteBleibt() && !leisteGesagt.current) {
              leisteGesagt.current = true;
              toast(
                'info',
                'Die Leiste des Browsers bleibt — Safari lässt für eine Leinwand kein echtes Vollbild zu. Ganz ohne sie geht es, wenn du die App über „Teilen → Zum Home-Bildschirm“ ablegst.',
              );
            }
            vollbild.umschalten();
          }}
          aria-label={vollbild.an ? 'Vollbild verlassen' : 'Vollbild'}
          title={vollbild.an ? 'Vollbild verlassen (Esc)' : 'Nur das Bild zeigen'}
        >
          <Icon name={vollbild.an ? 'fullscreenExit' : 'fullscreen'} size={18} />
        </button>
        <UndoRedo />
      </div>

      {role === 'viewer' ? (
        <div data-nur-lesen style={{ background: '#f59e0b26', color: 'var(--warn-text)', fontSize: 12, padding: '6px 12px' }}>
          Nur Lesezugriff — du kannst zusehen und exportieren lassen, aber nichts ändern.
        </div>
      ) : null}

      {/* Bühne und Transportleiste sind eine Karte: die Leiste schwebt als
          Glas auf dem Bild, statt als eigene Zeile darunter zu stehen. Das
          gibt auf dem Telefon eine ganze Zeile mehr Bild her (Saschas Entwurf
          vom 7. September). */}
      <div className={`buehne${pegelAn ? ' mit-pegel' : ''}`}>
        <Preview oeffneMedien={() => setSheet('media')} oeffneBlatt={(blatt) => setSheet(blatt)} />
        <Transport />
        <Pegelmesser />
        {role !== 'viewer' ? (
          <button
            className="format-knopf"
            data-format-knopf
            aria-label={`Bildformat ${formatKurz(format.width, format.height)}`}
            title="Bildformat"
            onClick={(e) => {
              const b = e.currentTarget.getBoundingClientRect();
              setFormatMenue({ x: b.right, y: b.bottom + 4 });
            }}
          >
            {formatKurz(format.width, format.height)}
          </button>
        ) : null}
        {formatMenue ? <ContextMenu x={formatMenue.x} y={formatMenue.y} items={formatItems} onClose={() => setFormatMenue(null)} align="right" /> : null}
      </div>
      {/* 7.74e: Solange der Modus läuft, steht hier, was zu tun ist -- und der
          Weg heraus. Ohne die Zeile wäre der Modus ein Zustand, in den man
          gerät, ohne ihn zu bemerken. */}
      {uebergangsModus ? (
        <div className="uebergang-leiste" data-uebergang-leiste>
          <span>
            {uebergangsStellen(timelineJetzt).length > 0
              ? 'Tipp auf ein + zwischen zwei Clips.'
              : 'Dafür müssen zwei Clips auf der Basisspur aneinanderliegen.'}
          </span>
          <button className="small" onClick={() => setUebergangsModus(false)}>
            Fertig
          </button>
        </div>
      ) : null}
      {platzieren ? (
        <div className="uebergang-leiste platzieren-leiste" data-platzieren-leiste>
          <span>
            Wohin mit {platzieren.namen.length === 1 ? `„${platzieren.namen[0]}“` : `${platzieren.namen.length} Dateien`}? Auf ein +
            tippen.
          </span>
          <button className="small" onClick={() => platziere(useEditor.getState().playhead, null)} data-platzieren-kopf>
            Am Kopf
          </button>
          {platzieren.art === 'geraet' ? (
            <button className="small" onClick={nurZuDenMedien} data-platzieren-medien>
              Nur Medien
            </button>
          ) : null}
          <button className="small" onClick={() => setPlatzieren(null)} data-platzieren-abbrechen>
            Abbrechen
          </button>
        </div>
      ) : null}
      <Timeline
        onAdd={() => setSheet('media')}
        onOpenSheet={(name) => {
          if (name === 'brandKit') setKitReturnsTo(null);
          if (name === 'spur') setSpurReturnsTo(null);
          setSheet(name);
        }}
      />
      <PasteBar />
      {/* Eine Leiste unten, nicht zwei: ist ein Clip gewählt, stehen dort
          seine Werkzeuge, sonst die des Projekts. Sascha: „eigentlich ist es
          ganz gut, dass die Symbolleiste unten ersetzt wird -- mir ging es um
          das Design, nicht um den Ort. Der nimmt jetzt Platz weg.“ Der Weg
          zurück steht als erster Knopf in der Reihe. */}
      <ClipTools
        onOpen={(name) => {
          if (name === 'brandKit') setKitReturnsTo(null);
          setSheet(name);
        }}
        active={sheet}
      />
      {/* Auf dem Telefon tritt die Clip-Leiste an ihre Stelle (das macht das
          Stilblatt); am großen Schirm ist oben Platz für beide. */}
      <Toolbar
        onOpen={(name) => {
          if (name === 'brandKit') setKitReturnsTo(null);
          setSheet(name);
        }}
        active={sheet}
        onBefehle={() => setBefehlsfeld(true)}
      />
      {/* Und links die Quellen -- erst ab 1024 px sichtbar, das regelt das
          Stilblatt. Unten fallen dieselben drei dort weg (7.25c). */}
      <QuellenLeiste
        onOpen={(name) => {
          if (name === 'brandKit') setKitReturnsTo(null);
          setSheet(name);
        }}
        active={sheet}
      />

      {sheet === 'media' ? (
        <MediaSheet
          onClose={() => setSheet(null)}
          onRecord={() => setSheet('voiceover')}
          onDiashow={() => setSheet('diashow')}
        />
      ) : null}
      {sheet === 'voiceover' ? <VoiceoverSheet onClose={() => setSheet(null)} /> : null}
      {sheet === 'uebergaenge' ? <UebergangSheet onClose={() => setSheet(null)} /> : null}
      {sheet === 'messgeraete' ? <MessgeraeteSheet onClose={() => setSheet(null)} /> : null}
      {sheet === 'clip' ? (
        <InspectorSheet
          onClose={() => setSheet(null)}
          onOpenSpur={(trackId) => {
            setSpurBlatt(trackId);
            setSpurReturnsTo('clip');
            setSheet('spur');
          }}
          onOpenTon={() => setSheet('audio')}
        />
      ) : null}
      {sheet === 'audio' ? <AudioSheet onClose={() => setSheet(null)} /> : null}
      {sheet === 'bereich' && bereichBlatt ? (
        <BereichSheet key={bereichBlatt} bereichId={bereichBlatt} onClose={() => setSheet(null)} />
      ) : null}
      {sheet === 'spur' && spurBlatt ? (
        <SpurSheet
          trackId={spurBlatt}
          onClose={() => setSheet(spurReturnsTo)}
          closeLabel={spurReturnsTo ? 'Zurück' : 'Fertig'}
        />
      ) : null}
      {sheet === 'brand' ? (
        <BrandSheet
          onClose={() => setSheet(null)}
          onEditKit={() => {
            setKitReturnsTo('brand');
            setSheet('brandKit');
          }}
          onAssistent={() => setSheet('markenAssistent')}
        />
      ) : null}
      {sheet === 'brandKit' ? (
        <BrandKitSheet
          onClose={() => setSheet(kitReturnsTo)}
          closeLabel={kitReturnsTo ? 'Zurück' : 'Fertig'}
          onAssistent={() => setSheet('markenAssistent')}
        />
      ) : null}
      {sheet === 'markenAssistent' ? (
        <MarkensetAssistent onClose={() => setSheet('brand')} onFertig={() => setSheet('brand')} />
      ) : null}
      {befehlsfeld ? <BefehlSheet befehle={befehle} onClose={() => setBefehlsfeld(false)} /> : null}
      {/* Alte, nie gesicherte Änderungen (7.220): fragen statt verwerfen. */}
      <AlteAenderungenSheet />
      {sheet === 'transcript' ? <TranscriptSheet onClose={() => setSheet(null)} /> : null}
      {sheet === 'diashow' ? <DiashowSheet onClose={() => setSheet('media')} /> : null}
      {sheet === 'export' ? <ExportSheet onClose={() => setSheet(null)} /> : null}
      {sheet === 'project' ? (
        <ProjectSettingsSheet
          onClose={() => setSheet(null)}
          onLeave={onBack}
          onManageAccess={() => {
            setTeilenAusProjekt(true);
            setSheet('teilen');
          }}
          onVerlauf={() => setSheet('verlauf')}
        />
      ) : null}
      {sheet === 'verlauf' ? <VerlaufSheet onClose={() => setSheet('project')} closeLabel="Zurück" /> : null}
      {sheet === 'teilen' ? (
        <TeilenSheet
          onClose={() => setSheet(teilenAusProjekt ? 'project' : null)}
          closeLabel={teilenAusProjekt ? 'Zurück' : undefined}
        />
      ) : null}
      {sheet === 'kommentare' ? (
        <KommentarSheet
          fokus={hinweisFokus}
          onClose={() => {
            setHinweisFokus(null);
            setSheet(null);
          }}
        />
      ) : null}

      {/* Zwischen „ausgesucht“ und „drin“ liegt die Frage nach der Stelle
          (7.49). Sie steht für sich, nicht in der Medienliste: dort geht es
          ums Aussuchen, hier ums Hinlegen. */}
      {einfuegeMedium ? <EinfuegenSheet onClose={() => setSheet(null)} /> : null}

      {feedback ? <FeedbackSheet bereich="Schnittplatz" onClose={() => setFeedback(false)} /> : null}

      {/* Dateien mit der Maus: die Ziele, und was gerade hochlädt (15. Oktober). */}
      <Ablegen fortschritt={sheet !== 'media'} />
      <Toasts />
    </div>
  );
}

const CONNECTION_LABEL = {
  live: 'Verbunden — Änderungen werden geteilt',
  connecting: 'Verbinde…',
  offline: 'Keine Verbindung — Änderungen warten',
} as const;

function Toasts(): ReactElement {
  const toasts = useEditor((s) => s.toasts);
  const dismiss = useEditor((s) => s.dismissToast);
  return (
    <div className="toasts">
      {toasts.map((toast) => (
        <div key={toast.id} className={`toast ${toast.kind}`} onClick={() => dismiss(toast.id)} role="status">
          {toast.message}
        </div>
      ))}
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
